/**
 * @file index.tsx
 *
 * @fileoverview Shows the gallery widget content when in view mode.
 */
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { VideoGalleryIcon } from '~/vcl/icons/VideoGallery';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import CMPServiceCheckbox from '~/jobiqo/components/CMPserviceCheckbox';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationVideoGalleryWidgetViewProps } from '~/jobiqo/components/Organization/Widgets/VideoGallery/View';
import VideoNotAvailableThumb from '~/jobiqo/components/Organization/Widgets/VideoGallery/View/components/VideoUnavailableThumb';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { VideoGallery } from '~/vcl/molecules/11-gallery/02-video-gallery';
import styles from './videoGalleryContent.module.scss';
import {
  checkVideoConsent,
  mapVideoTypeToService,
} from '~/jobiqo/components/Landingpage/utils/landingPage';

export const OrganizationVideoGalleryWidgetViewCotent = ({
  organization,
  boxId,
}: OrganizationVideoGalleryWidgetViewProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  return (
    <>
      {(organization?.videos?.length as number) > 0 && (
        <VideoGallery
          isGalleryOpen={isGalleryOpen}
          startIndex={startIndex}
          onClose={() => setIsGalleryOpen(false)}
          videos={(organization?.videos?.filter((videoItem) => {
            return videoItem
              ? checkVideoConsent(videoItem.type, userContext)
              : false;
          }) as any[]).map((videoItem) => {
            return {
              cover: videoItem?.cover?.url ?? '',
              src: videoItem?.source ?? '',
              type: videoItem?.type ?? '',
            };
          })}
        />
      )}
      <Grid columns={[1, 2, 3]} gap={7}>
        {(organization?.videos as any).map((videoItem, index) => {
          // If the video has no generated cover something went wrong.
          // In this case we show a thumbnail to tell the user the video is not
          // available.
          if (!videoItem?.cover?.url) {
            return (
              <Cell display="grid" key={index} gridRowGap={4}>
                <FlexItems justifyContent="flex-end">
                  <EditLink
                    organization={organization}
                    boxId={boxId}
                    fieldIndex={index}
                  />
                </FlexItems>
                <VideoNotAvailableThumb
                  organization={organization}
                  boxId={boxId}
                  index={index}
                />
              </Cell>
            );
          }

          return checkVideoConsent(videoItem.type, userContext) ? (
            <Cell display="grid" key={index} gridRowGap={4}>
              <FlexItems justifyContent="flex-end">
                <EditLink
                  organization={organization}
                  boxId={boxId}
                  fieldIndex={index}
                />
              </FlexItems>
              <button
                className={styles.imgButton}
                onClick={(e) => {
                  setIsGalleryOpen(true);
                  setStartIndex(index);
                }}
              >
                <img
                  alt={videoItem?.cover?.alt ?? ''}
                  {...(videoItem?.title && { title: videoItem.title })}
                  src={videoItem?.cover?.url ?? ''}
                  width="480"
                  height="360"
                />
                <FlexItems justifyContent="center" alignItems="center">
                  <VideoGalleryIcon
                    width="36"
                    height="36"
                    title={
                      <FormattedMessage
                        id="video_gallery_overlay_title"
                        defaultMessage="Play"
                      />
                    }
                  />
                </FlexItems>
              </button>
            </Cell>
          ) : (
            <Cell display="grid" key={index}>
              <Box>
                <FlexItems justifyContent="flex-end">
                  <EditLink
                    organization={organization}
                    boxId={boxId}
                    fieldIndex={index}
                  />
                </FlexItems>
                <AlignedCenter>
                  <Text marginTop="0">
                    <FormattedMessage
                      id="video_gallery_consent"
                      defaultMessage="Consent to this service"
                    />
                  </Text>
                  <Spacing />
                  <CMPServiceCheckbox
                    serviceId={mapVideoTypeToService(videoItem.type)}
                  />
                </AlignedCenter>
              </Box>
            </Cell>
          );
        })}
      </Grid>
    </>
  );
};
export default OrganizationVideoGalleryWidgetViewCotent;
